// 格子狀態（LIVE-04）：路徑（區網 / direct / relay）、codec、解析度、fps、位元率——從 RTCPeerConnection.getStats() 算。
// 路徑以選中的 candidate pair 判斷：任一端 relay → relay；兩端都是 host → 區網；其他（srflx / prflx）→ direct。
// 位元率要兩次取樣的差（bytesReceived / timestamp），第一次取樣回 null。

export type MediaPath = 'lan' | 'direct' | 'relay';

export interface TileStats {
  path: MediaPath | null;
  codec: string | null;
  width: number | null;
  height: number | null;
  fps: number | null;
  /** bit/s；第一次取樣沒有前一筆可以比 → null */
  bitrate: number | null;
}

/** 上一次取樣（算位元率用）。 */
export interface StatsSample {
  bytes: number;
  ts: number;
}

type Stat = Record<string, unknown> & { id: string; type: string };

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** RTCStatsReport（或測試用的陣列）→ 格子狀態；prev 是上一次的取樣，回傳這一次的取樣給下一輪。 */
// RTCStatsReport（Map：forEach 第一個參數是值）與陣列都用 forEach 走。
export function parseStats(report: { forEach(cb: (s: Stat) => void): void }, prev: StatsSample | null): { stats: TileStats; sample: StatsSample | null } {
  const byId = new Map<string, Stat>();
  const all: Stat[] = [];
  report.forEach((s) => {
    byId.set(s.id, s);
    all.push(s);
  });

  // 選中的 pair：transport.selectedCandidatePairId 優先，沒有就找 nominated + succeeded（Firefox 沒有 transport stats）
  let pair: Stat | undefined;
  const transport = all.find((s) => s.type === 'transport' && typeof s.selectedCandidatePairId === 'string');
  if (transport) pair = byId.get(transport.selectedCandidatePairId as string);
  pair ??= all.find((s) => s.type === 'candidate-pair' && (s.selected === true || (s.nominated === true && s.state === 'succeeded')));
  let path: MediaPath | null = null;
  if (pair) {
    const l = byId.get(pair.localCandidateId as string);
    const r = byId.get(pair.remoteCandidateId as string);
    const lt = l?.candidateType;
    const rt = r?.candidateType;
    if (lt === 'relay' || rt === 'relay') path = 'relay';
    else if (lt === 'host' && rt === 'host') path = 'lan';
    else if (lt || rt) path = 'direct';
  }

  const inbound = all.find((s) => s.type === 'inbound-rtp' && (s.kind === 'video' || s.mediaType === 'video'));
  let codec: string | null = null;
  if (inbound && typeof inbound.codecId === 'string') {
    const mime = byId.get(inbound.codecId)?.mimeType;
    if (typeof mime === 'string') codec = mime.replace(/^video\//i, '').toUpperCase();
  }
  const width = num(inbound?.frameWidth);
  const height = num(inbound?.frameHeight);
  const fpsRaw = num(inbound?.framesPerSecond);
  const fps = fpsRaw === null ? null : Math.round(fpsRaw);
  const bytes = num(inbound?.bytesReceived);
  const ts = num(inbound?.timestamp);
  let bitrate: number | null = null;
  let sample: StatsSample | null = null;
  if (bytes !== null && ts !== null) {
    sample = { bytes, ts };
    if (prev && ts > prev.ts && bytes >= prev.bytes) bitrate = ((bytes - prev.bytes) * 8 * 1000) / (ts - prev.ts);
  }
  return { stats: { path, codec, width, height, fps, bitrate }, sample };
}

/** 1234567 → "1.2 Mbps"；< 1 Mbps 用 kbps。 */
export function formatBitrate(bps: number): string {
  if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(1)} Mbps`;
  return `${Math.round(bps / 1000)} kbps`;
}
